import clsx from 'clsx';

export interface BadgeProps {
  bordered?: boolean;
  children?: React.ReactNode;
  color?: 'white' | 'black' | 'primary' | 'gray' | 'red' | 'yellow' | 'green' | 'blue';
  disabled?: boolean;
  icon?: React.ReactNode;
  onClickRemove?: React.MouseEventHandler;
  removable?: boolean;
  rounded?: boolean;
  size?: 'sm' | 'md';
  uppercase?: boolean;
}

export function Badge({
  bordered,
  children,
  color = 'gray',
  disabled,
  icon,
  onClickRemove,
  removable,
  rounded = true,
  size = 'md',
  uppercase = false
}: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center font-semibold whitespace-nowrap',

        rounded ? 'rounded-full' : 'rounded-md',

        size === 'sm' &&
          clsx(
            'py-0.5 text-[0.7rem]/4',
            rounded
              ? clsx('pl-2', removable ? 'pr-1' : 'pr-2')
              : clsx('pl-1.5', removable ? 'pr-0.5' : 'pr-1.5')
          ),
        size === 'md' &&
          clsx(
            'py-0.5 text-sm',
            rounded
              ? clsx('pl-3', removable ? 'pr-1.5' : 'pr-3')
              : clsx('pl-2', removable ? 'pr-1' : 'pr-2')
          ),

        bordered && 'border',

        color === 'white' && clsx('bg-white text-gray-800', bordered && 'border-gray-400'),
        color === 'black' && clsx('bg-gray-950 text-white', bordered && 'border-gray-950'),
        color === 'primary' &&
          clsx('bg-primary-100 text-primary-800', bordered && 'border-primary-400'),
        color === 'gray' && clsx('bg-gray-100 text-gray-800', bordered && 'border-gray-400'),
        color === 'red' && clsx('bg-red-100 text-red-800', bordered && 'border-red-400'),
        color === 'yellow' && clsx('bg-amber-100 text-amber-800', bordered && 'border-amber-400'),
        color === 'green' &&
          clsx('bg-emerald-100 text-emerald-800', bordered && 'border-emerald-400'),
        color === 'blue' && clsx('bg-blue-100 text-blue-800', bordered && 'border-blue-400'),

        uppercase && 'uppercase'
      )}
    >
      {icon && (
        <span
          className={clsx(
            size === 'sm' && 'mr-1 -ml-0.5 *:size-3',
            size === 'md' && 'mr-1.5 -ml-0.5 *:size-4'
          )}
        >
          {icon}
        </span>
      )}
      <span>{children}</span>

      {removable && (
        <button
          className={clsx(
            'inline-flex size-4 shrink-0 items-center justify-center rounded-full focus:outline-hidden',

            size === 'sm' && 'ml-0.5',
            size === 'md' && 'ml-1',

            color !== 'white' && 'focus:text-white',

            color === 'white' && clsx('text-gray-400', !disabled && 'hover:text-gray-700'),
            color === 'primary' &&
              clsx(
                'text-primary-400',
                !disabled && 'hover:bg-primary-200 hover:text-primary-500 focus:bg-primary-500'
              ),
            color === 'gray' &&
              clsx(
                'text-gray-400',
                !disabled && 'hover:bg-gray-200 hover:text-gray-500 focus:bg-gray-500'
              ),
            color === 'red' &&
              clsx(
                'text-red-400',
                !disabled && 'hover:bg-red-200 hover:text-red-500 focus:bg-red-500'
              ),
            color === 'yellow' &&
              clsx(
                'text-amber-400',
                !disabled && 'hover:bg-amber-200 hover:text-amber-500 focus:bg-amber-500'
              ),
            color === 'green' &&
              clsx(
                'text-emerald-400',
                !disabled && 'hover:bg-emerald-200 hover:text-emerald-500 focus:bg-emerald-500'
              ),
            color === 'blue' &&
              clsx(
                'text-blue-400',
                !disabled && 'hover:bg-blue-200 hover:text-blue-500 focus:bg-blue-500'
              )
          )}
          disabled={disabled}
          onClick={onClickRemove}
          type="button"
        >
          <svg className="size-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
            <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
          </svg>
        </button>
      )}
    </span>
  );
}
